import { getFormErrorMessage } from "@core/utility/get-error-message";
import { PackageAccommodationFormRule, PackageAccommodationFormSchema } from "@factory/form/form.rule";
import { useFormDefaultPackageAccommodation } from "@factory/form/form-default-package-accommodation.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponsePackageAccommodationModel } from "@factory/model/query-model-package-accommodation";
import { 
  useQyDeletePackageAccommodation, 
  useQyGetPackageAccommodationById, 
  useQyUpdatePackageAccommodation} from "@factory/query/package-accommodation.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditPackageAccommodation() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultPackageAccommodation();
  const navigate = useNavigate();
  const { mapUpdatePackageAccommodation, mapDeletePackageAccommodation } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { packageAccommodationId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID PackageAccommodation
  const handleGetApiSuccess = (data: GetResponsePackageAccommodationModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("package", responseData?.package || "");
      setValue("night", responseData?.night);
      setValue("hotel", responseData?.hotel || "");
      setValue("roomType", responseData?.room_type || "");
      setValue("numPeople", responseData?.num_people);
      setValue("unit", responseData?.unit || "");
      setValue("validFrom", responseData?.valid_from || "");
      setValue("validUntil", responseData?.valid_until || "");
      setValue("name", responseData?.name || "");
      setValue("price", responseData?.price);
      setValue("description", responseData?.description || "");
  };
  const {
    data: packageAccommodationResponse,
    isLoading,
    isError: packageAccommodationError,
  } = useQyGetPackageAccommodationById(packageAccommodationId || "", handleGetApiSuccess);
  const packageAccommodationData = packageAccommodationResponse?.data?.[0];

  // API UPDATE PackageAccommodation
  const handleUpdateApiSuccess = () => {
    showSuccess("PackageAccommodation updated successfully");
  };
  const { mutate: updatePackageAccommodation, isLoading: isEditLoading } = useQyUpdatePackageAccommodation(
    handleUpdateApiSuccess
  );

  // API DELETE PackageAccommodation
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("PackageAccommodation deleted successfully");
  };
  const { mutate: deletePackageAccommodation } = useQyDeletePackageAccommodation(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<PackageAccommodationFormSchema>({
    defaultValues: getDefault(packageAccommodationData),
    resolver: zodResolver(PackageAccommodationFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: PackageAccommodationFormSchema) => {
    if (!packageAccommodationData || !packageAccommodationData.code) {
      throw new Error("No packageAccommodation data");
    }

    const formData = mapUpdatePackageAccommodation(form, packageAccommodationData.code);
    updatePackageAccommodation(formData);
  };
  const handleValidateError = (err: FieldErrors<PackageAccommodationFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!packageAccommodationData || !packageAccommodationData.code) {
      throw new Error("No packageAccommodation data found");
    }

    const form = mapDeletePackageAccommodation(packageAccommodationData.code);
    deletePackageAccommodation(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    packageAccommodationId,
    packageAccommodationData,
    isLoading,
    packageAccommodationError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}